/**
 * Fund Code Enum Keys
 */
export enum FundCodeType {
  ALL_ACCOUNTS = 'ALL_ACCOUNTS',
  INDIVIDUAL = 'INDIVIDUAL',
  RETIREMENT = 'RETIREMENT',
}

/**
 * Fund Code Metadata Interface
 */
export interface FundCodeMetadata {
  key: FundCodeType;
  code: string;
  label: string;
}

/**
 * Fund Code Types with Code and Label
 */
export const FUND_CODES: Record<FundCodeType, FundCodeMetadata> = {
  [FundCodeType.ALL_ACCOUNTS]: {
    key: FundCodeType.ALL_ACCOUNTS,
    code: '00',
    label: 'All Accounts',
  },
  [FundCodeType.INDIVIDUAL]: {
    key: FundCodeType.INDIVIDUAL,
    code: '01',
    label: 'Individual',
  },
  [FundCodeType.RETIREMENT]: {
    key: FundCodeType.RETIREMENT,
    code: '02',
    label: 'Retirement',
  },
};

/**
 * Get all fund code labels as array
 * @returns Array of fund code labels ['All Accounts', 'Individual', 'Retirement']
 * @example
 * const labels = getFundCodeLabels();
 * // ['All Accounts', 'Individual', 'Retirement']
 */
export function getFundCodeLabels(): string[] {
  return Object.values(FUND_CODES).map((fund) => fund.label);
}

/**
 * Get all fund code values (codes) as array
 * @returns Array of fund code values ['00', '01', '02']
 * @example
 * const codes = getFundCodeValues();
 * // ['00', '01', '02']
 */
export function getFundCodeValues(): string[] {
  return Object.values(FUND_CODES).map((fund) => fund.code);
}

/**
 * Get fund code options for dropdown
 * @returns Array of {value, label} objects
 * @example
 * const options = getFundCodeOptions();
 * // [{value: '00', label: 'All Accounts'}, {value: '01', label: 'Individual'}, ...]
 */
export function getFundCodeOptions(): { value: string; label: string }[] {
  return Object.values(FUND_CODES).map((fund) => ({
    value: fund.code,
    label: fund.label,
  }));
}

/**
 * Get label by fund code
 * @param code - The fund code (e.g., '00', '01', '02')
 * @returns The fund code label (e.g., 'All Accounts', 'Individual') or empty string if not found
 * @example
 * const label = getFundCodeLabel('01'); // Returns 'Individual'
 * const label2 = getFundCodeLabel('02'); // Returns 'Retirement'
 */
export function getFundCodeLabel(code: string): string {
  const fundCode = Object.values(FUND_CODES).find((fund) => fund.code === code);
  return fundCode?.label || '';
}

/**
 * Get fund code by label
 * @param label - The fund code label (e.g., 'Individual', 'Retirement')
 * @returns The fund code (e.g., '01', '02') or empty string if not found
 * @example
 * const code = getFundCodeByLabel('Individual'); // Returns '01'
 * const code2 = getFundCodeByLabel('Retirement'); // Returns '02'
 */
export function getFundCodeByLabel(label: string): string {
  const fundCode = Object.values(FUND_CODES).find((fund) => fund.label === label);
  return fundCode?.code || '';
}

/**
 * Get fund code metadata by code
 * @param code - The fund code (e.g., '00', '01', '02')
 * @returns FundCodeMetadata object or undefined if not found
 * @example
 * const metadata = getFundCodeMetadata('01');
 * // { key: FundCodeType.INDIVIDUAL, code: '01', label: 'Individual' }
 */
export function getFundCodeMetadata(code: string): FundCodeMetadata | undefined {
  return Object.values(FUND_CODES).find((fund) => fund.code === code);
}

/**
 * Get fund code metadata by key
 * @param key - The fund code type key
 * @returns FundCodeMetadata object
 * @example
 * const metadata = getFundCodeMetadataByKey(FundCodeType.INDIVIDUAL);
 * // { key: FundCodeType.INDIVIDUAL, code: '01', label: 'Individual' }
 */
export function getFundCodeMetadataByKey(key: FundCodeType): FundCodeMetadata {
  return FUND_CODES[key];
}

/**
 * Check if value is a valid fund code
 * @param code - Value to check
 * @returns true if valid fund code
 * @example
 * isValidFundCode('01'); // true
 * isValidFundCode('99'); // false
 */
export function isValidFundCode(code: string): boolean {
  return Object.values(FUND_CODES).some((fund) => fund.code === code);
}

/**
 * Check if code is ALL_ACCOUNTS
 * @param code - Fund code to check
 * @returns true if code is ALL_ACCOUNTS ('00')
 */
export function isAllAccounts(code: string): boolean {
  return code === FUND_CODES[FundCodeType.ALL_ACCOUNTS].code;
}

/**
 * Check if code is INDIVIDUAL
 * @param code - Fund code to check
 * @returns true if code is INDIVIDUAL ('01')
 */
export function isIndividual(code: string): boolean {
  return code === FUND_CODES[FundCodeType.INDIVIDUAL].code;
}

/**
 * Check if code is RETIREMENT
 * @param code - Fund code to check
 * @returns true if code is RETIREMENT ('02')
 */
export function isRetirement(code: string): boolean {
  return code === FUND_CODES[FundCodeType.RETIREMENT].code;
}

/**
 * Get ALL_ACCOUNTS fund code
 * @returns '00'
 */
export function getAllAccountsFundCode(): string {
  return FUND_CODES[FundCodeType.ALL_ACCOUNTS].code;
}

/**
 * Get INDIVIDUAL fund code
 * @returns '01'
 */
export function getIndividualFundCode(): string {
  return FUND_CODES[FundCodeType.INDIVIDUAL].code;
}

/**
 * Get RETIREMENT fund code
 * @returns '02'
 */
export function getRetirementFundCode(): string {
  return FUND_CODES[FundCodeType.RETIREMENT].code;
}

/**
 * Get ALL_ACCOUNTS label
 * @returns 'All Accounts'
 */
export function getAllAccountsLabel(): string {
  return FUND_CODES[FundCodeType.ALL_ACCOUNTS].label;
}

/**
 * Get INDIVIDUAL label
 * @returns 'Individual'
 */
export function getIndividualLabel(): string {
  return FUND_CODES[FundCodeType.INDIVIDUAL].label;
}

/**
 * Get RETIREMENT label
 * @returns 'Retirement'
 */
export function getRetirementLabel(): string {
  return FUND_CODES[FundCodeType.RETIREMENT].label;
}

/**
 * Get fund codes excluding 'All Accounts'
 * @returns Array of specific account type codes ['01', '02']
 */
export function getSpecificFundCodes(): string[] {
  return [FUND_CODES[FundCodeType.INDIVIDUAL].code, FUND_CODES[FundCodeType.RETIREMENT].code];
}

/**
 * Get fund code options excluding 'All Accounts'
 * @returns Array of {value, label} objects for specific account types
 */
export function getSpecificFundCodeOptions(): { value: string; label: string }[] {
  return [
    {
      value: FUND_CODES[FundCodeType.INDIVIDUAL].code,
      label: FUND_CODES[FundCodeType.INDIVIDUAL].label,
    },
    {
      value: FUND_CODES[FundCodeType.RETIREMENT].code,
      label: FUND_CODES[FundCodeType.RETIREMENT].label,
    },
  ];
}
